import { Meal } from "@/clients/models/Meal";
import { MealTime } from "@/clients/models/MealTime";
import { PlansService } from "@/clients/services/PlansService";
import { QueryKeys } from "@/constants/query-keys";
import { preloadImage } from "@/utils/preload-image";
import { guardGetUserFromLocalStorage } from "@/utils/request";
import { useQuery } from "@tanstack/react-query";

export const useGetAlternativePlans = (
  mealId: string,
  mealTime: MealTime,
  mealDay?: string,
  filter?: string,
  subFilter?: string,
  fromBrands?: string[],
  searchTerm?: string,
  isExtra?: boolean,
  targetDate?: string,
) => {
  const normalizedDay = mealDay?.toLowerCase() ?? undefined;
  const normalizedBrands = fromBrands?.filter((value) => value && value.length > 0) ?? [];
  const uniqueBrands = Array.from(new Set(normalizedBrands));
  const brandsKey = uniqueBrands.length > 0 ? uniqueBrands.slice().sort().join(",") : "";
  const normalizedSearch = searchTerm?.trim() ?? "";

  const { data, isLoading, isError, error, refetch, isFetching } = useQuery({
    queryKey: [
      QueryKeys.PLAN_ALTERNATIVE,
      mealId,
      mealTime,
      normalizedDay ?? "",
      filter ?? "",
      subFilter ?? "",
      brandsKey,
      normalizedSearch,
      isExtra ?? false,
      targetDate ?? "",
    ],
    refetchOnMount: false,
    refetchOnWindowFocus: false,
    enabled: !!mealId && !!mealTime,
    queryFn: async () => {
      const user = guardGetUserFromLocalStorage();

      const fromBrand = uniqueBrands.length > 0 ? uniqueBrands : undefined;

      const response = await PlansService.getMealAlternativesApiV1PlansMealAlternativesGet(
        mealId,
        mealTime,
        user.id,
        normalizedDay,
        filter,
        subFilter,
        fromBrand,
        normalizedSearch || undefined,
        isExtra || undefined,
        targetDate,
      );

      const { data } = response;
      const meals = data?.meals?.flatMap((mealGroup) => mealGroup.mealTime?.data ?? []) ?? [];

      const seenImageUris = new Set<string>();
      const preloadMealsInRange = (start: number, end: number) => {
        const clampedStart = Math.max(0, start);
        const clampedEnd = Math.min(meals.length - 1, end);
        for (let index = clampedStart; index <= clampedEnd; index += 1) {
          const meal = meals[index] as Meal | undefined;
          const imageUri = meal?.imageUri;
          if (!imageUri || seenImageUris.has(imageUri)) {
            continue;
          }
          seenImageUris.add(imageUri);
          preloadImage(imageUri);
        }
      };

      const activeIndex = meals.findIndex((meal) => meal?.id === mealId);

      if (activeIndex !== -1) {
        preloadMealsInRange(activeIndex - 3, activeIndex + 3);
      } else {
        preloadMealsInRange(0, 2);
      }

      return response;
    },
  });

  return {
    plans: data?.data,
    requestId: isFetching ? null : (data?.data?.requestId ?? null),
    isLoading,
    isFetching,
    isError,
    error,
    refetch,
  };
};
